import { FontIcon, Layer, PersonaSize, Stack, useTheme } from '@fluentui/react';
import { FC, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { IAgentReport } from '../../models/reports.internal';
import { nameof } from '../../utils/stringUtils';
import useTimeFormatters from '../../shared/hooks/useTimeFormatters';
import { InlineDiv } from '../../shared/styled-components';
import CardContainer from './CardContainer';
import ElapsedTimeSpan  from './ElapsedTimeSpan';
import { ActivityDiv, ActivityText, StyledPersona, TaskMetrics } from './AgentCard.components';
import ExpandedAgentCard from './AgentCard.extended';
import { getActivityDetails } from './AgentCard.helpers';

//#region [1] StateDisplay
const RawStateDisplay = styled.div`
  background-color: ${p => p.theme.palette.neutralLight};

  padding: 8px;
  border-radius: 8px;
  display: flex;
  gap: 6px;
  justify-content: space-between;
  align-items: flex-end;
  flex-shrink: 0;
`;

const StateDisplayBar = styled.div<{ percentage: number; color: string }>`
  border-radius: 2px;
  width: 12px;
  height: max(1px, ${p => p.percentage}%);
  background-color: ${p => p.color};
  opacity: ${p => p.percentage === 0 && '50%'};
`;

interface IStateDisplayProps {
  timeSpentInStates: IAgentReport['timeSpentInStates'];
}

const StateDisplay: FC<IStateDisplayProps> = ({ timeSpentInStates }) => {
  const { palette } = useTheme();

  const sum = useMemo(
    () => (timeSpentInStates.Busy + timeSpentInStates.Break + timeSpentInStates.Idle) || 1,
    [timeSpentInStates.Break, timeSpentInStates.Busy, timeSpentInStates.Idle],
  );

  return (
    <RawStateDisplay>
      <StateDisplayBar color={palette.red} percentage={Math.ceil(timeSpentInStates.Busy / sum * 100)} />
      <StateDisplayBar color={palette.yellow} percentage={Math.ceil(timeSpentInStates.Break / sum * 100)} />
      <StateDisplayBar color={palette.green} percentage={Math.ceil(timeSpentInStates.Idle / sum * 100)} />
    </RawStateDisplay>
  );
};
//#endregion

const OverlayContainer = styled.div`
  height: 100vh;
  background-color: ${p => p.theme.palette.blackTranslucent40};
  display: relative;

  @keyframes layerFadeIn {
    from {
      opacity: 0;
      pointer-events: none;
    }
    to {
      opacity: 1;
    }
  }

  animation: layerFadeIn 0.3s ease;
`;

const StyledCardContainer = styled(CardContainer)`
  display: flex;
  gap: 0.5rem;

  cursor: pointer;

  @keyframes agentCardAppear {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }

  animation: agentCardAppear 0.3s ease;
`;

export const DetailsDiv = styled.div`
  flex-grow: 1;
  flex-shrink: 1;
  min-width: 1px;
`;

interface IProps {
  agent: IAgentReport;
}

const AgentCard: FC<IProps> = ({ agent }) => {
  const { formatTime } = useTimeFormatters();

  const [expanded, setExpanded] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const containerBoundingRectangle = containerRef.current?.getBoundingClientRect();

  const activity = useMemo(
    () => getActivityDetails(agent.currentState, agent.breakName, agent.currentlyHandledWorkItems),
    [agent.breakName, agent.currentState, agent.currentlyHandledWorkItems],
  );

  return (
    <div>
      <StyledCardContainer ref={containerRef} onClick={() => setExpanded(true)}>
        <DetailsDiv>
          <Stack verticalAlign="space-between" styles={{ root: { height: '100%' } }}>
            <StyledPersona size={PersonaSize.size56}
              presence={activity.presence}
              text={agent.name}
              onRenderSecondaryText={() => (
                <InlineDiv gap="0.75rem">
                  <InlineDiv gap="0.25rem">
                    <FontIcon iconName="Signin" style={{ fontSize: 16 }} />
                    <span>{agent.loggedInTimestamp ? formatTime(agent.loggedInTimestamp) : ''}</span>
                  </InlineDiv>
                  {
                    agent.loggedOutTimestamp &&
                    <InlineDiv gap="0.25rem">
                      <FontIcon iconName="SignOut" style={{ fontSize: 16 }} />
                      <span>{formatTime(agent.loggedOutTimestamp)}</span>
                    </InlineDiv>
                  }
                </InlineDiv>
              )}
            />
            <ActivityDiv>
              {activity.icon && <FontIcon iconName={activity.icon} />}
              <ActivityText textStyle={activity.textStyle}>{activity.text}</ActivityText>
              <span>(<ElapsedTimeSpan startTimestamp={agent.currentStateSince} />)</span>
            </ActivityDiv>
          </Stack>
        </DetailsDiv>
        <TaskMetrics agent={agent} />
        <StateDisplay timeSpentInStates={agent.timeSpentInStates} />
      </StyledCardContainer>
      {
        expanded &&
        <Layer>
          <OverlayContainer onClick={(e) => { if (e.currentTarget === e.target) setExpanded(false); }}>
            <ExpandedAgentCard
              agent={agent}
              activity={activity}
              containerBoundingRectangle={containerBoundingRectangle}
            />
          </OverlayContainer>
        </Layer>
      }
    </div>
  );
};

AgentCard.displayName = nameof({ AgentCard });
export default AgentCard;